import { chakra, Flex, useColorModeValue } from "@chakra-ui/react";
import { ReactNode } from "react";
import { LegendSwatch, LineLegendItem } from "./LineLegend";

export type ToggleLegendItem = LineLegendItem & {
  key: string;
  hidden?: boolean;
  /** Extra controls shown after the item, e.g. a details button. */
  actions?: ReactNode;
};

/**
 * Legend whose items show or hide their series on click and highlight it on
 * hover or keyboard focus.
 */
export const ToggleLegend = ({
  items,
  direction = "row",
  onToggle,
  onHighlight,
}: {
  items: ToggleLegendItem[];
  direction?: "row" | "column";
  onToggle: (key: string) => void;
  onHighlight: (key: string | undefined) => void;
}) => {
  const hoverBg = useColorModeValue("blackAlpha.50", "whiteAlpha.100");

  return (
    <Flex
      direction={direction}
      flexWrap="wrap"
      columnGap={2}
      rowGap={1}
      fontSize="sm"
      onMouseLeave={() => onHighlight(undefined)}
    >
      {items.map(({ key, label, hidden, actions, ...swatch }) => (
        <Flex key={key} alignItems="center" gap={1}>
          <chakra.button
            type="button"
            aria-pressed={!hidden}
            title={hidden ? `Show ${label}` : `Hide ${label}`}
            display="flex"
            alignItems="center"
            gap={2}
            px={2}
            py={1}
            borderRadius="md"
            textAlign="left"
            _hover={{ bg: hoverBg }}
            onClick={() => onToggle(key)}
            onMouseEnter={() => onHighlight(hidden ? undefined : key)}
            onFocus={() => onHighlight(hidden ? undefined : key)}
            onBlur={() => onHighlight(undefined)}
          >
            <LegendSwatch {...swatch} opacity={hidden ? 0.3 : 1} />
            <chakra.span
              whiteSpace="nowrap"
              opacity={hidden ? 0.5 : 1}
              textDecoration={hidden ? "line-through" : undefined}
            >
              {label}
            </chakra.span>
          </chakra.button>
          {actions}
        </Flex>
      ))}
    </Flex>
  );
};
